"use client";

import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import Link from "next/link";
import { InventoryCollectionItem } from "./inventory-collection";
import type { InventoryListProps } from "./types";

export function InventoryList({
  collections,
  isLoading,
  error,
  className,
}: InventoryListProps) {
  if (error) {
    return (
      <div className={className}>
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span className="text-xs">
            Failed to load inventory. Please try again.
          </span>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="space-y-3">
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={`skeleton-${crypto.randomUUID()}`}
              className="bg-white/5 backdrop-blur-sm rounded-lg p-3"
            >
              <div className="flex items-center gap-3">
                <Skeleton className="w-8 h-8 rounded bg-white/10" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32 bg-white/10" />
                  <Skeleton className="h-3 w-20 bg-white/10" />
                </div>
                <Skeleton className="h-4 w-16 bg-white/10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="text-center py-8">
          <div className="text-white/40 text-xs">No collections found</div>
        </div>

        <div className="flex justify-center pt-2">
          <Link
            href="/inventory"
            className="text-white/60 hover:text-white text-xs underline transition-colors"
          >
            More Details
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="space-y-3">
        {collections.map((collection) => (
          <InventoryCollectionItem
            key={collection.address}
            collection={collection}
          />
        ))}
      </div>

      <div className="flex justify-center pt-2">
        <Link
          href="/inventory"
          className="text-white/60 hover:text-white text-xs underline transition-colors"
        >
          More Details
        </Link>
      </div>
    </div>
  );
}
